/* ==========================================================================
   Product gallery pro (7Roars)
   - Prev / next arrows on the main product media, layout aware
   - Zoom dialog in the Pirloni layout: one media at a time, control pill
   - Takes over Dawn's own lightbox instead of fighting it
   ========================================================================== */

(function () {
  'use strict';

  function isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /* Layouts where Dawn shows every media at once, so is-active must not be
     used to switch — arrows only scroll between them. */
  function isStackedDesktop(gallery) {
    const layout = gallery.dataset.desktopLayout || '';
    return !isMobile() && (layout === 'stacked' || layout === 'columns');
  }

  /* Dawn puts the composed id ("sectionId-mediaId") on the <li> but the RAW
     media id on the lightbox toggle button. Match on both. */
  function findSlide(modal, mediaId) {
    if (!mediaId) return null;
    return (
      modal.querySelector('[data-media-id="' + mediaId + '"]') ||
      modal.querySelector('[data-media-id$="-' + mediaId + '"]')
    );
  }

  /* ------------------------------------------------------------------------
     Zoom dialog
     ------------------------------------------------------------------------ */
  if (typeof customElements !== 'undefined' && !customElements.get('product-zoom-modal')) {
    class ProductZoomModal extends HTMLElement {
      connectedCallback() {
        // Re-entrant: appendChild below triggers this again, the flag stops it.
        if (this.ready) return;
        this.ready = true;

        this.init();

        // Rendered inside the product section, which may sit in a sticky or
        // transformed ancestor. That traps position:fixed under the header.
        if (this.parentElement !== document.body) {
          document
            .querySelectorAll('body > product-zoom-modal[data-section="' + this.dataset.section + '"]')
            .forEach((twin) => {
              if (twin !== this) twin.remove();
            });
          document.body.appendChild(this);
        }
      }

      init() {
        this.stage = this.querySelector('[data-pgp-stage]');
        this.items = Array.from(this.querySelectorAll('[data-pgp-slide]'));
        this.currentOutput = this.querySelector('[data-pgp-current]');
        this.clickZoom = this.dataset.clickZoom !== 'false';
        this.index = 0;
        this.lastFocus = null;

        this.onKeydown = this.onKeydown.bind(this);

        this.querySelectorAll('[data-pgp-close]').forEach((el) => {
          el.addEventListener('click', () => this.close());
        });

        const prev = this.querySelector('[data-pgp-prev]');
        const next = this.querySelector('[data-pgp-next]');
        if (prev) prev.addEventListener('click', () => this.step(-1));
        if (next) next.addEventListener('click', () => this.step(1));

        this.items.forEach((item) => {
          const image = item.querySelector('.pgp-zoom__img');
          if (!image) return;

          image.addEventListener('click', (event) => this.toggleZoom(event, item, image));
          image.addEventListener('mousemove', (event) => this.pan(event, item, image));
          image.addEventListener('mouseleave', () => this.resetZoom(item));
        });

        this.bindSwipe();
      }

      /* ---------- open / close ---------- */
      open(index) {
        if (this.classList.contains('is-open')) return;

        this.lastFocus = document.activeElement;
        this.hidden = false;
        document.body.classList.add('overflow-hidden');

        this.goTo(index || 0, true);

        requestAnimationFrame(() => {
          this.classList.add('is-open');
          this.stage.focus({ preventScroll: true });
        });

        document.addEventListener('keydown', this.onKeydown);
      }

      close() {
        this.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onKeydown);

        this.items.forEach((item) => this.resetZoom(item));

        window.setTimeout(() => {
          this.hidden = true;
        }, 260);

        if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
          this.lastFocus.focus({ preventScroll: true });
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        }
      }

      /* ---------- navigation ---------- */
      step(direction) {
        this.goTo(this.index + direction);
      }

      goTo(index, immediate) {
        if (!this.items.length) return;

        let next = index;
        if (next < 0) next = this.items.length - 1;
        if (next > this.items.length - 1) next = 0;

        this.items.forEach((item, i) => {
          this.resetZoom(item);
          item.classList.toggle('is-current', i === next);
        });

        this.index = next;
        if (this.currentOutput) this.currentOutput.textContent = next + 1;

        // Nudge the page gallery along so both stay in sync when the dialog closes.
        if (!immediate) this.syncGallery();
      }

      syncGallery() {
        const gallery = document.getElementById('MediaGallery-' + this.dataset.section);
        if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

        const mediaId = this.items[this.index] && this.items[this.index].dataset.mediaId;
        if (mediaId) gallery.setActiveMedia(mediaId);
      }

      bindSwipe() {
        let startX = 0;
        let startY = 0;

        this.stage.addEventListener(
          'touchstart',
          (event) => {
            startX = event.changedTouches[0].clientX;
            startY = event.changedTouches[0].clientY;
          },
          { passive: true }
        );

        this.stage.addEventListener(
          'touchend',
          (event) => {
            const dx = event.changedTouches[0].clientX - startX;
            const dy = event.changedTouches[0].clientY - startY;
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
              this.step(dx < 0 ? 1 : -1);
            }
          },
          { passive: true }
        );
      }

      /* ---------- zoom ---------- */
      toggleZoom(event, item, image) {
        if (isMobile() || !this.clickZoom) return;

        if (item.classList.contains('is-zoomed')) {
          this.resetZoom(item);
          return;
        }

        item.classList.remove('is-panning');
        this.setOrigin(event, item, image);
        item.classList.add('is-zoomed');
      }

      pan(event, item, image) {
        if (!item.classList.contains('is-zoomed')) return;
        item.classList.add('is-panning');
        this.setOrigin(event, item, image);
      }

      setOrigin(event, item, image) {
        const rect = image.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;

        item.style.setProperty('--pgp-ox', Math.min(100, Math.max(0, x)) + '%');
        item.style.setProperty('--pgp-oy', Math.min(100, Math.max(0, y)) + '%');
      }

      resetZoom(item) {
        item.classList.remove('is-zoomed', 'is-panning');
      }
    }

    customElements.define('product-zoom-modal', ProductZoomModal);
  }

  /* ------------------------------------------------------------------------
     Gallery helpers
     ------------------------------------------------------------------------ */
  function getViewer(gallery) {
    return (
      gallery.querySelector('[id^="GalleryViewer"]') ||
      gallery.querySelector('slider-component') ||
      gallery.querySelector('.product__media-list')
    );
  }

  function getItems(viewer) {
    // Only the media <li>. Dawn's .product__media-toggle button also carries a
    // data-media-id, so a bare [data-media-id] query returns twice the items.
    const items = Array.from(viewer.querySelectorAll('.product__media-item'));
    return items.length ? items : Array.from(viewer.querySelectorAll('li[data-media-id]'));
  }

  function getModal(gallery) {
    const sectionId = (gallery.id || '').replace('MediaGallery-', '');
    if (!sectionId) return null;
    return document.getElementById('ProductZoom-' + sectionId);
  }

  function currentIndex(gallery, items) {
    if (isStackedDesktop(gallery)) {
      // Whichever media is nearest the middle of the screen.
      const middle = window.innerHeight / 2;
      let best = 0;
      let bestDistance = Infinity;
      items.forEach((item, i) => {
        const rect = item.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - middle);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    const index = items.findIndex((item) => item.classList.contains('is-active'));
    return index < 0 ? 0 : index;
  }

  function navigate(gallery, viewer, direction) {
    const items = getItems(viewer);
    if (items.length < 2) return;

    let next = currentIndex(gallery, items) + direction;
    if (next < 0) next = items.length - 1;
    if (next > items.length - 1) next = 0;

    const target = items[next];

    // Stacked / columns on desktop: every media is already on screen, so only
    // scroll. Touching is-active here is what made the media disappear.
    if (isStackedDesktop(gallery)) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const mediaId = target.dataset.mediaId;

    if (typeof gallery.setActiveMedia === 'function' && mediaId) {
      gallery.setActiveMedia(mediaId);
    }

    // Guarantee the state even if the theme's method bailed out early, so the
    // gallery can never end up with nothing active (blank media).
    if (!target.classList.contains('is-active')) {
      items.forEach((item) => item.classList.remove('is-active'));
      target.classList.add('is-active');
    }

    // Horizontal slider layouts (mobile, thumbnail slider) need an explicit scroll.
    const slider = viewer.querySelector('.slider') || target.parentElement;
    if (slider && slider.scrollWidth > slider.clientWidth + 2) {
      slider.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }
  }

  function makeArrow(direction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pgp-arrow pgp-arrow--' + (direction < 0 ? 'prev' : 'next');
    button.setAttribute('aria-label', direction < 0 ? 'Previous media' : 'Next media');
    button.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="' +
      (direction < 0 ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7') +
      '"/></svg>';
    return button;
  }

  /* Dawn binds its lightbox listener straight onto the opener's <button> in the
     ModalOpener constructor, so stopPropagation cannot reach it. Replacing the
     button with a clone drops that listener and leaves ours in charge. */
  function releaseThemeOpeners(gallery) {
    gallery.querySelectorAll('.product__modal-opener').forEach((opener) => {
      const button = opener.querySelector('button');
      if (!button || button.dataset.pgpReleased === 'true') return;

      const clone = button.cloneNode(true);
      clone.dataset.pgpReleased = 'true';
      button.replaceWith(clone);
    });
  }

  /* Final safety net: if anything at all still calls Dawn's product-modal,
     redirect it to ours instead of letting the old lightbox paint. */
  function disableThemeModal(gallery, zoom) {
    const sectionId = (gallery.id || '').replace('MediaGallery-', '');
    const themeModal = document.getElementById('ProductModal-' + sectionId);
    if (!themeModal || themeModal.dataset.pgpDisabled === 'true') return;

    themeModal.dataset.pgpDisabled = 'true';
    themeModal.show = function (opener) {
      const mediaId = opener && opener.dataset ? opener.dataset.mediaId : null;
      const slide = findSlide(zoom, mediaId);
      zoom.open(slide ? parseInt(slide.dataset.pgpSlide, 10) || 0 : 0);
    };
    themeModal.hide = function () {};
  }

  function enhance(gallery) {
    if (gallery.dataset.pgpReady === 'true') return;

    const viewer = getViewer(gallery);
    if (!viewer) return;

    gallery.dataset.pgpReady = 'true';
    gallery.classList.add('pgp-ready');
    viewer.classList.add('pgp-viewer');

    const zoom = getModal(gallery);
    if (zoom) {
      releaseThemeOpeners(gallery);
      disableThemeModal(gallery, zoom);
    }

    if (getItems(viewer).length < 2) return;

    const prev = makeArrow(-1);
    const next = makeArrow(1);

    prev.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      navigate(gallery, viewer, -1);
    });

    next.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      navigate(gallery, viewer, 1);
    });

    viewer.appendChild(prev);
    viewer.appendChild(next);
  }

  function enhanceAll() {
    document.querySelectorAll('media-gallery').forEach(enhance);
  }

  /* ------------------------------------------------------------------------
     Click on the main media opens our zoom dialog
     ------------------------------------------------------------------------ */
  document.addEventListener(
    'click',
    function (event) {
      const viewer = event.target.closest('.pgp-viewer');
      if (!viewer) return;

      // Leave our arrows, video posters and 3D buttons alone.
      if (event.target.closest('.pgp-arrow')) return;
      if (event.target.closest('.deferred-media__poster')) return;
      if (event.target.closest('button[class*="poster"]')) return;
      if (event.target.closest('.product__xr-button')) return;

      const item =
        event.target.closest('.product__media-item') || event.target.closest('[data-media-id]');
      if (!item) return;

      const gallery = viewer.closest('media-gallery');
      if (!gallery) return;

      const modal = getModal(gallery);
      if (!modal || typeof modal.open !== 'function') return;

      const clicked = event.target.closest('[data-media-id]');
      const slide =
        findSlide(modal, item.dataset.mediaId) ||
        findSlide(modal, clicked && clicked.dataset ? clicked.dataset.mediaId : null);
      if (!slide || slide.dataset.type !== 'image') return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      modal.open(parseInt(slide.dataset.pgpSlide, 10) || 0);
    },
    true
  );

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceAll);
  } else {
    enhanceAll();
  }

  // Dawn re-renders the gallery / product info on variant change.
  new MutationObserver(function (mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.tagName === 'MEDIA-GALLERY') enhance(node);
        else if (node.querySelector && node.querySelector('media-gallery')) enhanceAll();
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', enhanceAll);

  // The dialog lives on <body>, so clean it up ourselves in the theme editor.
  document.addEventListener('shopify:section:unload', function (event) {
    const id = event.detail && event.detail.sectionId;
    if (!id) return;
    const modal = document.getElementById('ProductZoom-' + id);
    if (modal && modal.parentElement === document.body) modal.remove();
  });
})();